import { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import { FieldArray } from 'formik'
import { cloneDeep } from 'lodash'

import { Format } from 'services/Dates'
import AvailabilityTime from 'components/Requests/AvailabilityTime'
import { useModal } from 'components/Hooks'
import { Radio, Button } from 'components/Forms'
import { Alert, Button as MerinioButton, ButtonGroup, Card, Checkbox, Dropdown } from 'ui'

import { t } from 'translations'
import styles from './request.module.scss'
import clsx from 'clsx'

type Props = {
  day: MData.Day
  disabled?: boolean
  setFieldValue: (field: string, value: unknown) => void
  restrictAvailability?: boolean
  restrictedAvailabilityBlocks?: string[]
  isManager?: boolean
}

const AvailabilityItem: FC<Props> = ({ day, disabled, setFieldValue, restrictAvailability, restrictedAvailabilityBlocks, isManager }) => {
  const { defaultBeginningOfWeekDay } = useSelector((state: MStore.RootState) => state.settings)
  const { scheduleTypesById, nonFilterScheduleTypes } = useSelector((state: MStore.RootState) => state.resources)
  const [CopyModal, openCopyModal] = useModal()

  const [copyDays, setCopyDays] = useState([
    { code: Format('1', { outFormat: 'eeeee', inFormat: 'e' }), toggled: false },
    { code: Format('2', { outFormat: 'eeeee', inFormat: 'e' }), toggled: false },
    { code: Format('3', { outFormat: 'eeeee', inFormat: 'e' }), toggled: false },
    { code: Format('4', { outFormat: 'eeeee', inFormat: 'e' }), toggled: false },
    { code: Format('5', { outFormat: 'eeeee', inFormat: 'e' }), toggled: false },
    { code: Format('6', { outFormat: 'eeeee', inFormat: 'e' }), toggled: false },
    { code: Format('7', { outFormat: 'eeeee', inFormat: 'e' }), toggled: false }
  ])

  let dayOrder: MData.Day['dayOfWeek'][] = [0, 1, 2, 3, 4, 5, 6]
  if (defaultBeginningOfWeekDay ?? 0 !== 0) dayOrder = [...dayOrder.slice(defaultBeginningOfWeekDay), ...dayOrder.slice(0, defaultBeginningOfWeekDay)]

  const toggleCopyDay = (index: MData.Day['dayOfWeek']) => {
    copyDays[index].toggled = !copyDays[index].toggled
    setCopyDays(cloneDeep(copyDays))
  }

  const applyCopiedDays = () => {
    copyDays.forEach((copyDay, index) => {
      if (copyDay.toggled) {
        // Overwrite the availabilities of the selected day with this one
        setFieldValue(`days[${index}].availabilities`, cloneDeep(day.availabilities))
        setFieldValue(`days[${index}].notAvailable`, day.notAvailable)
      }
    })
    // Toggle off the copy mode
    cancelCopyDays()
  }

  const cancelCopyDays = () => {
    // Toggle off all days
    for (const copyDay of copyDays) {
      copyDay.toggled = false
    }

    setCopyDays(cloneDeep(copyDays))
  }

  let unMatchedAvailabilities: MData.Day['availabilities'] = []
  if (restrictAvailability && restrictedAvailabilityBlocks) {
    unMatchedAvailabilities = day.availabilities.filter(
      ({ start, end, type }) =>
        !restrictedAvailabilityBlocks.some(scheduleTypeId => {
          const { startTime, endTime } = scheduleTypesById[scheduleTypeId]
          return start === startTime && end === endTime && type === 'AVAILABLE'
        })
    )
  }
  return (
    <div>
      <h5>{Format(`${day.dayOfWeek + 1}`, { outFormat: 'humanWeekDay', inFormat: 'e' })}</h5>
      {restrictAvailability && restrictedAvailabilityBlocks ? (
        <>
          {restrictedAvailabilityBlocks.map(scheduleTypeId => {
            const { name, startTime, endTime } = scheduleTypesById[scheduleTypeId]
            const checked = day.availabilities.some(({ start, end, type }) => start === startTime && end === endTime && type === 'AVAILABLE')

            return (
              <Card
                key={scheduleTypeId}
                className="p-1 px-2 mb-1 text-left"
                data-cy={`scheduleType-block-${scheduleTypeId}`}
                onClick={() => {
                  const availabilities: MData.Day['availabilities'] = [
                    !checked
                      ? {
                          start: startTime,
                          end: endTime,
                          type: 'AVAILABLE'
                        }
                      : null,
                    ...day.availabilities.filter(({ start, end, type }) => {
                      // Remove identical availabilities (to avoid duplicates)
                      return !(start === startTime && end === endTime && type === 'AVAILABLE')
                    })
                  ].filter(Boolean)
                  setFieldValue(`days.${day.dayOfWeek}.availabilities`, availabilities)
                  setFieldValue(`days.${day.dayOfWeek}.notAvailable`, false)
                }}
              >
                <div className="form-check">
                  {/* This is really just a fake checkbox, to provide visual feedback to the user */}
                  <Checkbox readOnly checked={checked} data-cy={`checkbox-${scheduleTypeId}`} />
                  <label className="form-check-label">
                    {name}
                    <span className="text-muted ml-1">
                      ({startTime}-{endTime})
                    </span>
                  </label>
                </div>
              </Card>
            )
          })}

          <Alert variant="warning" className="mb-0 text-left" show={!!unMatchedAvailabilities?.length}>
            <div>
              <div className="mt-2">{t('employee.availabilityForm.UNMATCHED_AVAILABILITIES_WARNING')}</div>
              <ul className="mt-2">
                {unMatchedAvailabilities.map(({ start, end, type }, index) => (
                  <li key={index}>
                    {start}-{end} {t(`employee.availabilityForm.${type}`)}
                  </li>
                ))}
              </ul>
            </div>
          </Alert>
        </>
      ) : (
        <>
          <Radio
            module="employee.availabilityForm"
            name={`days.${day.dayOfWeek}.notAvailable`}
            options={[false, true]}
            noLabel
            buttonRadios
            translation="notAvailable"
            verticalButtonGroup
            disabled={disabled}
          />
          <FieldArray name={`days.${day.dayOfWeek}.availabilities`}>
            {arrayHelpers => {
              return (
                <div>
                  {day.availabilities.map((timeSlot, timeSlotIndex) => (
                    <AvailabilityTime
                      key={timeSlotIndex}
                      day={day}
                      type={timeSlot.type}
                      timeSlotIndex={timeSlotIndex}
                      handleDelete={() => arrayHelpers.remove(timeSlotIndex)}
                      setFieldValue={setFieldValue}
                    />
                  ))}
                  <ButtonGroup className="mr-2 w-100" vertical>
                    <Button
                      className="btn btn-outline-secondary"
                      onClick={() => arrayHelpers.push({ start: '09:00', end: '17:00', type: !day.notAvailable ? 'AVAILABLE' : 'UNAVAILABLE' })}
                      disabled={disabled}
                      data-cy="availabilities-specify-range"
                    >
                      <i className="fas fa-clock mr-1"></i>
                      {t(`employee.availabilityForm.ADD_${day.availabilities.length ? 'ANOTHER_' : ''}RANGE`)}
                    </Button>
                    {isManager ? (
                      <>
                        <Dropdown.Toggle id="scheduleTypes-dropdown" color="secondary" outlined>
                          <i className="fas fa-calendar mr-1"></i>
                          {t('manager.availabilities.form.SCHEDULE_TYPES')}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {nonFilterScheduleTypes.map(({ name, startTime, endTime }) => (
                            <Dropdown.Item
                              key={name}
                              as={Button}
                              onClick={() =>
                                arrayHelpers.push({
                                  start: startTime,
                                  end: endTime,
                                  type: !day.notAvailable ? 'AVAILABLE' : 'UNAVAILABLE'
                                })
                              }
                              disabled={day.availabilities.some(({ start, end }) => start === startTime && end === endTime)}
                            >
                              {name} ({startTime}-{endTime})
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </>
                    ) : null}
                  </ButtonGroup>
                </div>
              )
            }}
          </FieldArray>
        </>
      )}

      <div>
        {day.availabilities.length ? (
          <MerinioButton type="button" color="secondary" className="w-100 mt-2" onClick={openCopyModal} outlined data-cy="availabilities-copy-button">
            {t('actions.COPY')}
          </MerinioButton>
        ) : null}
      </div>
      <CopyModal title="employee.availabilityForm.COPY_MODAL" primaryAction={applyCopiedDays} secondaryAction={cancelCopyDays}>
        {
          <>
            <div>{t('employee.availabilityForm.COPY_TO_FOLLOWING', { day: Format(`${day.dayOfWeek + 1}`, { outFormat: 'humanWeekDay', inFormat: 'e' }) })}</div>
            <div>
              {copyDays.filter(copyDay => copyDay.toggled).length
                ? copyDays
                    .map((copyDay, index) => (copyDay.toggled ? Format(`${index + 1}`, { outFormat: 'humanWeekDay', inFormat: 'e' }) : null))
                    .filter(Boolean)
                    .join(', ')
                : t(`employee.availabilityForm.NONE`)}
            </div>
            <div className="d-flex justify-content-start my-2">
              {dayOrder.map(dayOfWeek => (
                <div
                  key={dayOfWeek}
                  className={clsx(styles['day-toggle-circle'], 'mr-3', copyDays[dayOfWeek].toggled && styles['toggled'])}
                  onClick={() => toggleCopyDay(dayOfWeek)}
                  data-cy={`availabilities-copy-day-${dayOfWeek}`}
                >
                  {copyDays[dayOfWeek].code}
                </div>
              ))}
            </div>
          </>
        }
      </CopyModal>
    </div>
  )
}

export default AvailabilityItem
